import React, { useState } from 'react';
import axios from 'axios';
import { FaLock, FaArrowLeft, FaCheck, FaUserPlus } from 'react-icons/fa';
import { useColorMode } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ROLE_OPTIONS = [
  { value: 'communication', label: 'Communication', desc: 'Gestion du blog, journal et ressources' },
  { value: 'commercial', label: 'Commercial', desc: 'Accès aux demandes de contact et devis' },
  { value: 'rh', label: 'Ressources Humaines', desc: 'Consultation des candidatures (CV)' },
];

export default function DemandeAcces() {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const navigate = useNavigate();

  const [form, setForm] = useState({ nom: '', email: '', mdp: '', requested_roles: [] });
  const [showMdp, setShowMdp] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const toggleRole = (r) => setForm(p => ({
    ...p,
    requested_roles: p.requested_roles.includes(r)
      ? p.requested_roles.filter(x => x !== r)
      : [...p.requested_roles, r],
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.requested_roles.length === 0) { setError('Sélectionnez au moins un rôle souhaité.'); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/register-request`, form);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'envoi.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px', borderRadius: '12px', fontSize: '14px',
    border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
    background: isDark ? '#1e293b' : '#f8faff',
    color: isDark ? '#f1f5f9' : '#0f172a',
    outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '40px 16px',
      background: isDark ? '#0a0f16' : '#f0f4f8',
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        style={{
          width: '100%', maxWidth: '460px', borderRadius: '24px', padding: '40px',
          background: isDark ? '#111827' : '#ffffff',
          boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
          border: `1px solid ${isDark ? '#1e293b' : '#e2e8f0'}`,
        }}
      >
        {success ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#22c55e15', border: '2px solid #22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#22c55e' }}>
              <FaCheck size={28} />
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 900, margin: '0 0 8px', color: isDark ? '#f1f5f9' : '#0f172a' }}>Demande envoyée !</h2>
            <p style={{ opacity: 0.6, fontSize: '14px', margin: '0 0 24px', lineHeight: 1.6 }}>
              Un administrateur va examiner votre demande et vous contacter. Vous recevrez un accès une fois votre compte validé.
            </p>
            <button onClick={() => navigate('/connexion')} style={{ padding: '12px 24px', borderRadius: '12px', background: '#2563eb', color: 'white', fontWeight: 700, border: 'none', cursor: 'pointer', fontSize: '14px' }}>
              Retour à la connexion
            </button>
          </div>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: 'white' }}>
                <FaUserPlus size={24} />
              </div>
              <h2 style={{ margin: 0, fontSize: '1.6rem', fontWeight: 900, color: isDark ? '#f1f5f9' : '#0f172a' }}>Demande d'accès</h2>
              <p style={{ margin: '8px 0 0', opacity: 0.5, fontSize: '14px' }}>
                Votre demande sera examinée par un administrateur
              </p>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <input
                type="text" placeholder="Nom et prénom *" value={form.nom} required maxLength={60}
                onChange={e => setForm(p => ({ ...p, nom: e.target.value }))}
                style={inputStyle}
              />
              <input
                type="email" placeholder="Adresse email *" value={form.email} required maxLength={100}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                style={inputStyle}
              />
              <div style={{ position: 'relative' }}>
                <input
                  type={showMdp ? 'text' : 'password'}
                  placeholder="Mot de passe * (12+ car., MAJ, chiffre, @)"
                  value={form.mdp} required
                  onChange={e => setForm(p => ({ ...p, mdp: e.target.value }))}
                  style={{ ...inputStyle, paddingRight: '44px' }}
                />
                <button type="button" onClick={() => setShowMdp(v => !v)}
                  style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', opacity: 0.4, fontSize: '16px' }}>
                  {showMdp ? '🙈' : '👁'}
                </button>
              </div>

              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                  Rôle(s) souhaité(s) *
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {ROLE_OPTIONS.map(({ value, label, desc }) => {
                    const checked = form.requested_roles.includes(value);
                    return (
                      <label key={value} style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
                        background: checked ? '#2563eb08' : (isDark ? '#1e293b' : '#f8faff'),
                        border: `1px solid ${checked ? '#2563eb40' : (isDark ? '#334155' : '#e2e8f0')}`,
                        transition: 'all 0.15s',
                      }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleRole(value)}
                          style={{ accentColor: '#2563eb', width: '15px', height: '15px', cursor: 'pointer', flexShrink: 0 }}
                        />
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '13px', color: checked ? '#2563eb' : (isDark ? '#f1f5f9' : '#0f172a') }}>{label}</div>
                          <div style={{ fontSize: '11px', opacity: 0.5, marginTop: '2px' }}>{desc}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <AnimatePresence>
                {error && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                    style={{ padding: '10px 14px', borderRadius: '10px', background: '#ef444415', border: '1px solid #ef444430', color: '#ef4444', fontSize: '13px', fontWeight: 600 }}>
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              <button type="submit" disabled={loading}
                style={{
                  padding: '13px', borderRadius: '12px', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  background: loading ? '#94a3b8' : '#2563eb', color: 'white',
                  fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                }}
              >
                <FaLock size={13} /> {loading ? 'Envoi en cours…' : 'Envoyer la demande'}
              </button>

              <button type="button" onClick={() => navigate('/connexion')}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5, fontSize: '13px', fontWeight: 600, color: isDark ? '#f1f5f9' : '#0f172a' }}>
                <FaArrowLeft size={11} /> Retour à la connexion
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
